/**
 * Suppression d'une valeur de référentiel Qualité (QUAL-001 §5.3).
 *
 * Garde d'usage : `GET .../{id}/usage` → { usage_count, usage_count_own,
 * usage_count_descendants, can_delete }. Le décompte porte sur le périmètre des
 * FAITS (entité courante ∪ entités rattachées) : retirer une valeur du siège touche
 * les fiches des agences, et le taire rendait le chiffre incompréhensible.
 *
 * Si des non-conformités portent le code, le serveur EXIGE une réaffectation
 * (`reassign_to`) — la modale l'exige donc aussi, au lieu de promettre un « vider le
 * champ » qu'il refuse. Une valeur système (`is_system`, `can_delete=false`) n'est
 * pas supprimable, seulement désactivable ailleurs.
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { toastApiError } from '@/lib/toast-error'
import {
    useDeleteQualityReferential,
    useQualityReferentials,
    useQualityReferentialUsage,
} from '@/hooks/useQualityReferentials'
import type { QualityReferentialItem, QualityReferentialType } from '@/hooks/useQuality.types'

interface Props {
    target: Pick<QualityReferentialItem, 'id' | 'label' | 'is_system'> | null
    refType: QualityReferentialType
    entityId: string | null
    /** Fermeture SANS suppression (Annuler, Échap, clic hors modale). */
    onClose: () => void
    /** Suppression EFFECTUÉE. `reassignedTo` = code de remplacement, ou `null`
     *  quand AUCUNE non-conformité ne portait le code (rien à réaffecter). */
    onDeleted?: (reassignedTo: string | null) => void
}

export function QualityReferentialDeleteDialog({
    target,
    refType,
    entityId,
    onClose,
    onDeleted,
}: Props) {
    const { t } = useTranslation()
    const deleteMutation = useDeleteQualityReferential(entityId)
    const {
        data: usage,
        isPending: usagePending,
        isError: usageError,
    } = useQualityReferentialUsage(target?.id ?? null, entityId, { enabled: !!target })
    const { data: list } = useQualityReferentials(entityId, refType)
    const [reassignTo, setReassignTo] = useState<string>('')

    if (!target) return null

    // ⚠️ `usage?.usage_count ?? 0` seul faisait AFFIRMER « aucune non-conformité ne
    // l'utilise » à partir d'une requête encore en vol ou en ERREUR. Un état inconnu
    // n'est pas un zéro : tant qu'on ne sait pas, on ne supprime pas et on le dit.
    const usageKnown = !usagePending && !usageError && usage != null
    const usageCount = usage?.usage_count ?? 0
    const usageInDescendants = usage?.usage_count_descendants ?? 0
    const canDelete = usage?.can_delete ?? !target.is_system
    const candidates = (list?.items ?? []).filter((i) => i.id !== target.id && i.is_active)
    // Le serveur EXIGE une valeur de remplacement dès qu'une non-conformité porte le
    // code (`ReferentialInUseError`). La modale promettait l'inverse — « supprimez
    // sans réaffecter, le champ sera vidé » — et le serveur refusait. On dit
    // désormais la règle réelle, et le bouton reste inerte tant qu'elle n'est pas
    // satisfaite : une classification ne disparaît pas d'un dossier d'audit.
    const needsReassign = usageKnown && canDelete && usageCount > 0
    const noCandidate = needsReassign && candidates.length === 0
    const confirmDisabled =
        !canDelete ||
        !usageKnown ||
        deleteMutation.isPending ||
        (needsReassign && !reassignTo)

    const handleConfirm = async () => {
        try {
            const reassigned = reassignTo || null
            await deleteMutation.mutateAsync({ id: target.id, reassignTo: reassigned })
            toast.success(t('quality.referentials.deleteSuccess', 'Valeur supprimée'))
            if (onDeleted) onDeleted(reassigned)
            else onClose()
        } catch (err) {
            toastApiError(err, t('quality.referentials.deleteError', 'Impossible de supprimer la valeur'))
        }
    }

    return (
        <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('quality.referentials.deleteTitle', 'Supprimer la valeur')}</DialogTitle>
                    <DialogDescription>
                        {!canDelete
                            ? t(
                                  'quality.referentials.systemLocked',
                                  '« {{name}} » est une valeur système : elle ne peut pas être supprimée, seulement désactivée.',
                                  { name: target.label },
                              )
                            : usagePending
                              ? t(
                                    'quality.referentials.usageLoading',
                                    'Recherche des non-conformités classées sous « {{name}} »…',
                                    { name: target.label },
                                )
                              : usageError
                                ? t(
                                      'quality.referentials.usageError',
                                      'Impossible de savoir combien de non-conformités sont classées sous « {{name}} ». Sans ce décompte, la suppression reste bloquée : réessayez dans un instant.',
                                      { name: target.label },
                                  )
                                : noCandidate
                                  ? t(
                                        'quality.referentials.deleteNoCandidate',
                                        '« {{name}} » classe {{count}} non-conformité(s) et c\'est la seule valeur de cette liste. Créez d\'abord celle qui la remplacera.',
                                        { name: target.label, count: usageCount },
                                    )
                                  : usageCount > 0
                                    ? usageInDescendants > 0
                                      ? t(
                                            'quality.referentials.deleteWarningWithDescendants',
                                            '« {{name}} » classe {{count}} non-conformité(s), dont {{descendants}} dans les entités rattachées. Choisissez la valeur qui les remplacera : une non-conformité ne reste pas sans classification.',
                                            {
                                                name: target.label,
                                                count: usageCount,
                                                descendants: usageInDescendants,
                                            },
                                        )
                                      : t(
                                            'quality.referentials.deleteWarning',
                                            '« {{name}} » classe {{count}} non-conformité(s). Choisissez la valeur qui les remplacera : une non-conformité ne reste pas sans classification.',
                                            { name: target.label, count: usageCount },
                                        )
                                    : t(
                                          'quality.referentials.deleteConfirm',
                                          '« {{name}} » sera supprimée. Aucune non-conformité ne l\'utilise.',
                                          { name: target.label },
                                      )}
                    </DialogDescription>
                </DialogHeader>

                {needsReassign && candidates.length > 0 && (
                    <div className="space-y-2 py-2">
                        <Label>{t('quality.referentials.reassignTo', 'Réaffecter vers…')}</Label>
                        <Select value={reassignTo} onValueChange={setReassignTo}>
                            <SelectTrigger>
                                <SelectValue
                                    placeholder={t(
                                        'quality.referentials.reassignPlaceholder',
                                        'Choisir la valeur de remplacement',
                                    )}
                                />
                            </SelectTrigger>
                            <SelectContent>
                                {/* Le serveur résout la cible par son CODE (clé
                                    naturelle : les NC référencent `origin_code`, pas
                                    un id) — envoyer `c.id` produisait un 404 muet et
                                    rendait toute valeur utilisée indéboulonnable. */}
                                {candidates.map((c) => (
                                    <SelectItem key={c.id} value={c.code}>
                                        {c.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                <DialogFooter>
                    <Button type="button" variant="outline" onClick={onClose}>
                        {t('common.cancel', 'Annuler')}
                    </Button>
                    <Button
                        type="button"
                        variant="destructive"
                        disabled={confirmDisabled}
                        onClick={handleConfirm}
                    >
                        {t('common.delete', 'Supprimer')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
